/**
 * Inline text-edit source resolution and commit for `<fp-text>` blocks.
 *
 * A rendered element is editable when the block it sits in resolves back to an
 * authored (source-less) `<fp-text>` element or a bare-text `<fp-cell>`: that
 * element's body IS the markdown the session edits. A bound
 * `<fp-text source="...">` renders a quote field, so it is authored through
 * the field, not on the canvas.
 *
 * The session itself runs in a TipTap editor seeded from that markdown
 * (textEditModel.ts); committing writes the serialized markdown back into the
 * element and re-serializes the stored HTML - the same splice renders the
 * session's unsaved draft into the preview while it is open. Everything here is framework-free
 * and fully unit-testable, like documentEditor.ts.
 */

import {
  blockIdOf,
  parseDocumentHtml,
  resolveBlock,
  serializeDocumentHtml,
  type DocumentEdit,
} from "./documentEditor";
import { dedentAuthoredBody } from "./FpText";
import {
  authoredSourceMarkdown,
  markdownToEditorDoc,
  type EditorDoc,
  type FieldResolver,
} from "./textEditModel";

/**
 * The source offsets a paginated text fragment renders, captured when its edit
 * session opens, with the dedented body slice they covered at that moment.
 */
export interface SourceTextRange {
  start: number;
  end: number;
  text: string;
}

/**
 * Whether a RENDERED element can be inline text-edited.
 * @param html - The stored document HTML
 * @param renderedTarget - The candidate element in the rendered preview
 * @returns True when a text edit on this element can be committed to source
 */
export function canEditRenderedText(
  html: string,
  renderedTarget: Element,
): boolean {
  return resolveSourceEditTarget(html, renderedTarget) !== null;
}

/**
 * Resolves the SOURCE `<fp-text>` element an inline edit session covers, for
 * seeding the session's editor doc. The returned element belongs to a
 * throwaway parsed document - read from it, don't mutate it.
 * @param html - The stored document HTML
 * @param renderedTarget - The element entering inline editing
 * @returns The authored fp-text element, or null when there is none
 */
export function resolveSourceEditTarget(
  html: string,
  renderedTarget: Element,
): HTMLElement | null {
  return resolveAuthoredText(parseDocumentHtml(html), renderedTarget);
}

/**
 * Reads the source slice a paginated text fragment renders, once, as its edit
 * session opens. Offsets index the same dedented body the renderer cut from.
 * @param source - The authored source element the session covers
 * @param renderedTarget - The rendered element entering inline editing
 * @returns The fragment's range and slice, or null for an unpaginated block
 */
export function readSourceTextRange(
  source: Element,
  renderedTarget: Element,
): SourceTextRange | null {
  const offsets = renderedTextOffsets(renderedTarget);
  if (!offsets) {
    return null;
  }
  return {
    ...offsets,
    text: authoredBody(source).slice(offsets.start, offsets.end),
  };
}

/**
 * The stored, dedented body of an authored source element: the text every
 * fragment's offsets index.
 * @param source - The authored source element
 * @returns The dedented body
 */
export function authoredBody(source: Element): string {
  return dedentAuthoredBody(source.textContent ?? "");
}

/**
 * The markdown a session edits: only the fragment's slice when the session is
 * ranged, otherwise the source element's whole body.
 * @param source - The authored source element the session covers
 * @param range - The fragment range captured at open, or null
 * @returns The markdown seeding the TipTap session
 */
export function sessionMarkdown(
  source: Element,
  range: SourceTextRange | null,
): string {
  return range ? range.text : authoredSourceMarkdown(source);
}

/**
 * Loads a session's editor doc from the markdown it edits.
 * @param source - The authored source element the session covers
 * @param range - The fragment range captured at open, or null
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor document seeding the TipTap session
 */
export function loadSessionEditorDoc(
  source: Element,
  range: SourceTextRange | null,
  resolveField: FieldResolver,
): EditorDoc {
  return markdownToEditorDoc(sessionMarkdown(source, range), resolveField);
}

/**
 * Commits an inline text edit: writes the session's markdown back into its
 * source `<fp-text>` - spliced over the session's range when it has one - and
 * re-serializes the HTML. Also renders an open session's draft into the
 * preview, from the same stored HTML and range.
 * @param html - The stored document HTML
 * @param renderedTarget - The rendered element the session is anchored to
 * @param markdown - The session's markdown for its whole range
 * @param range - The stored range the session edits, or null for a whole body
 * @returns The edit with the block reselected, or null when it can't commit
 */
export function commitInlineTextEdit(
  html: string,
  renderedTarget: Element,
  markdown: string,
  range: SourceTextRange | null,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const source = resolveAuthoredText(doc, renderedTarget);
  if (!source) {
    return null;
  }
  const body = editedBody(source, markdown, range);
  if (body === null) {
    return null;
  }
  source.textContent = body;
  return { html: serializeDocumentHtml(doc), nodeId: blockIdOf(doc, source) };
}

/**
 * The source body after an edit: the markdown alone for a whole-body session,
 * or the markdown spliced over a ranged session's slice.
 * @param source - The authored source element being committed
 * @param markdown - The session's committed markdown
 * @param range - The fragment range captured at open, or null
 * @returns The new body, or null when the slice changed since the session opened
 */
function editedBody(
  source: Element,
  markdown: string,
  range: SourceTextRange | null,
): string | null {
  if (!range) {
    return markdown;
  }
  const body = authoredBody(source);
  if (body.slice(range.start, range.end) !== range.text) {
    return null;
  }
  return body.slice(0, range.start) + markdown + body.slice(range.end);
}

/**
 * Finds the authored text source element behind a rendered element, by the
 * positional id stamped on the block it sits in: an authored (source-less)
 * `<fp-text>`, or an `<fp-cell>` carrying bare text - a bare-text cell renders
 * its textContent through the same markdown path as an `<fp-text>` body, so
 * both commit the same way.
 * @param doc - The parsed source document
 * @param renderedTarget - The element in the rendered preview
 * @returns The authored source element, or null
 */
function resolveAuthoredText(
  doc: Document,
  renderedTarget: Element,
): HTMLElement | null {
  const nodeId = renderedTarget
    .closest("[data-fp-node]")
    ?.getAttribute("data-fp-node");
  if (!nodeId) {
    return null;
  }
  const block = resolveBlock(doc, nodeId);
  if (!block) {
    return null;
  }
  const tag = block.tagName.toLowerCase();
  if (tag === "fp-text" && !block.hasAttribute("source")) {
    return block;
  }
  if (tag === "fp-cell" && holdsOnlyTokenizableText(block)) {
    return block;
  }
  return null;
}

/**
 * Whether a cell's body is expressible as the markdown an edit session works
 * in: bare text, plus `<fp-field>` children carrying nothing but a source -
 * those seed into the session as `{{ source }}` chips and commit back as
 * tokens. A cell nesting anything else (an fp-text, a styled field) keeps its
 * own editing path.
 * @param cell - The authored `<fp-cell>` element
 * @returns True when the cell's body round-trips through markdown
 */
function holdsOnlyTokenizableText(cell: Element): boolean {
  return Array.from(cell.children).every(
    (child) =>
      child.tagName.toLowerCase() === "fp-field" &&
      child.getAttributeNames().every((name) => name === "source"),
  );
}

/**
 * Picks the paginated fragment holding a body offset: the last one starting
 * at or before it, so an offset in the gap between two fragments stays with
 * the earlier one's end.
 * @param fragments - The block's rendered fragments, in page order
 * @param offset - The body offset to locate
 * @returns The holding fragment, or the first one when the offset precedes all
 */
export function fragmentHoldingOffset<T extends Element>(
  fragments: T[],
  offset: number,
): T | null {
  return (
    fragments.reduce<T | null>((holding, fragment) => {
      const start = renderedTextOffsets(fragment)?.start;
      return start !== undefined && start <= offset ? fragment : holding;
    }, null) ??
    fragments[0] ??
    null
  );
}

/**
 * Reads the source offsets stamped on a paginated text fragment.
 * @param target - The rendered element, or one inside the fragment
 * @returns The fragment's start and end offsets, or null when unstamped
 */
export function renderedTextOffsets(
  target: Element,
): { start: number; end: number } | null {
  const block = target.closest("[data-fp-text-start][data-fp-text-end]");
  if (!block) {
    return null;
  }
  const start = Number(block.getAttribute("data-fp-text-start"));
  const end = Number(block.getAttribute("data-fp-text-end"));
  return Number.isInteger(start) &&
    Number.isInteger(end) &&
    start >= 0 &&
    end >= start
    ? { start, end }
    : null;
}
